/**
 * This file defines how to interact with "Monarch" to setup global tests
 * or personalization/decisions outside of Preamp tests.
 *
 * @see https://redventures.atlassian.net/wiki/spaces/COHSN/pages/159089595/Monarch+Rules+Engine
 */
// @TODO: There is no use-case for this yet, but if we want to use Monarch any more we should abstract this rules engine code
;(() => {
  let matches = {}

  /**
   * Add a tag to the page source.
   *
   * @param {Element} tag - The tag to be added.
   * @param {string} contents - The contents to add to the tag.
   * @param {Element} [parent] - The parent element (optional).
   */
  function inlineTag(tag, contents, parent) {
    if (!tag || !contents) {
      throw Error('Invalid number of arguments.')
    }
    try {
      tag.appendChild(document.createTextNode(contents))
      parent.appendChild(tag)
    } catch (e) {
      tag.text = contents
      parent.appendChild(tag)
    }
  }

  /**
   * Add HTML to the page source.
   *
   * @param {string} htmlString - HTML source as a string (minified, please)
   * @param {string} parent - HTML parent container querySelector
   */
  function addHTML(htmlString, parent) {
    document.querySelector(parent).insertAdjacentHTML('afterend', htmlString)
  }

  /**
   * Add a style tag to the page source.
   *
   * @param {string} cssString - CSS source as a string (minified, please)
   * @param {string} [id] - ID to add to tag (optional)
   */
  function addCSS(cssString, id) {
    const style = document.createElement('style')
    const head = document.head || document.getElementsByTagName('head')[0]
    style.type = 'text/css'
    if (id) {
      style.setAttribute('data-id', id)
    }
    inlineTag(style, cssString, head)
  }

  /**
   * Add an inline script tag to the page.
   *
   * @param {string} jsString - JS source as a string (minified, please)
   * * @param {string} [id] - ID to add to tag (optional)
   */
  function addJS(jsString, id) {
    const body = document.body || document.getElementsByTagName('body')[0]
    const script = document.createElement('script')
    script.type = 'text/javascript'
    if (id) {
      script.setAttribute('data-id', id)
    }
    inlineTag(script, jsString, body)
  }

  const handleDecision = () => {
    if (Object.keys(matches).length < 1) {
      return
    }

    /**
     * RV Rules Engine Unsupported Browser Banner Case
     * - "0" indicates the control experience => do nothing
     * - "1" indicates the test experience => Show the Unsupported Browser Banner
     * @name "Unsupported Browser Check"
     * @id "2URIy92WfK0Dn3MfJZDAS7"
     * @see https://redventures.atlassian.net/browse/BRC-405
     */
    if ('2URIy92WfK0Dn3MfJZDAS7' in matches) {
      const isTest = parseInt(matches['2URIy92WfK0Dn3MfJZDAS7'], 10)
      if (isTest) {
        addCSS(
          '.unsupported-browser-banner .page-wrapper{width:100%;}.unsupported-browser-banner__close{display:flex;width:.75rem;margin-left:.625rem;padding:0;border:0;background:0;cursor:pointer;right:5px;top:5px}@media (min-width:768px){.unsupported-browser-banner__close{width:.9375rem;right:15px;top:15px}}.unsupported-browser-banner__close svg{width:.75rem;height:.75rem}@media (min-width:768px){.unsupported-browser-banner__close svg{width:.9375rem;height:.9375rem}}',
          '2URIy92WfK0Dn3MfJZDAS7'
        )
        addHTML(
          `<div class="unsupported-browser-banner grid +pd-all +pd-md +bg-gray-light">
              <div class="page-wrapper">
                  <div class="grid +bg-white +pd-vertical +pd-vertical-md +pd-top-md +pd-bottom-md +pd-left +pd-left-md +pd-right-lg +brdr-all-gray +pos-relative">
                      <p class="+type-body-one +mg-bottom-none">
                          <span class="icon --icon-sm +fg-blue">
                              <svg 
                                  class="icon__glyph"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 32 32"
                                  focusable="false"
                              ><path d="M 24.757 23.988 c 0.563 0 1.048 -0.18 1.454 -0.538 c 0.407 -0.36 0.657 -0.797 0.75 -1.312 a 2.375 2.375 0 0 0 -0.234 -1.523 L 15.47 1.125 c -0.281 -0.5 -0.68 -0.836 -1.196 -1.008 a 2.389 2.389 0 0 0 -1.548 0 c -0.516 0.172 -0.915 0.508 -1.196 1.007 L 0.273 20.615 c -0.25 0.5 -0.328 1.007 -0.235 1.523 c 0.094 0.515 0.344 0.952 0.75 1.312 c 0.407 0.359 0.892 0.538 1.455 0.538 h 22.514 Z m -0.469 -2.249 H 2.712 c -0.094 0 -0.172 -0.046 -0.235 -0.14 a 0.234 0.234 0 0 1 0 -0.281 L 13.265 2.67 c 0.063 -0.094 0.141 -0.14 0.235 -0.14 s 0.172 0.046 0.235 0.14 l 10.788 18.647 a 0.234 0.234 0 0 1 0 0.28 c -0.063 0.095 -0.141 0.141 -0.235 0.141 Z m -9.803 -6.371 a 0.493 0.493 0 0 0 0.352 -0.164 c 0.11 -0.11 0.18 -0.227 0.21 -0.352 l 0.282 -5.247 a 0.446 0.446 0 0 0 -0.14 -0.422 a 0.584 0.584 0 0 0 -0.423 -0.187 h -2.532 a 0.584 0.584 0 0 0 -0.423 0.187 a 0.446 0.446 0 0 0 -0.14 0.422 l 0.281 5.247 a 0.759 0.759 0 0 0 0.211 0.352 c 0.11 0.109 0.227 0.164 0.352 0.164 h 1.97 Z m -0.985 4.591 c 0.532 0 0.993 -0.195 1.384 -0.586 c 0.39 -0.39 0.586 -0.85 0.586 -1.382 c 0 -0.53 -0.195 -0.991 -0.586 -1.382 a 1.893 1.893 0 0 0 -1.384 -0.586 c -0.532 0 -0.993 0.196 -1.384 0.586 c -0.39 0.39 -0.586 0.851 -0.586 1.382 c 0 0.531 0.195 0.992 0.586 1.382 c 0.391 0.39 0.852 0.586 1.384 0.586 Z" />
                              </svg>
                          </span>
                          Hmm... It looks like you're using an outdated or unsupported browser that may not render correctly. For the best experience, we recommend using the latest version of Google Chrome, Safari, Microsoft Edge, or Firefox.
                      </p>
                      <button type="button" class="unsupported-browser-banner__close +pos-absolute"><svg viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg"><path fill="#333" d="M8.675 7.5l6.19 6.19a.66.66 0 0 1 .135.406.488.488 0 0 1-.136.362l-.406.406a.489.489 0 0 1-.362.136.66.66 0 0 1-.406-.136L7.5 8.63l-6.19 6.234A.66.66 0 0 1 .904 15a.489.489 0 0 1-.362-.136l-.406-.406A.489.489 0 0 1 0 14.096a.66.66 0 0 1 .136-.406L6.37 7.5.136 1.31A.66.66 0 0 1 0 .904C0 .753.045.633.136.542L.542.136A.49.49 0 0 1 .904 0a.66.66 0 0 1 .406.136l6.19 6.19 6.19-6.19A.66.66 0 0 1 14.096 0c.151 0 .271.045.362.136l.406.406A.49.49 0 0 1 15 .904a.66.66 0 0 1-.136.406L8.674 7.5z"/></svg></button>
                  </div>
              </div>
          </div>`,
          'header'
        )
        addJS(
          `"use strict";
            function createCookie(e, r, o) {
                var n = void 0,
                    s = void 0;
                (s = o ? ((n = new Date()).setTime(n.getTime() + 24 * o * 60 * 60 * 1e3), "; expires=" + n.toGMTString()) : ""), (document.cookie = e + "=" + r + s + "; path=/");
            }
            function deleteCookie(e) {
                createCookie(e, "", -1);
            }
            if (
                document.cookie.split(";").filter(function (e) {
                    return e.includes("unsupportedBrowserBannerDismissed=1");
                }).length
            ) {
                var _unsupportedBrowserBanner = document.querySelector(".unsupported-browser-banner");
                _unsupportedBrowserBanner.parentNode.removeChild(_unsupportedBrowserBanner);
            } else {
              var unsupportedBrowserBanner = document.querySelector(".unsupported-browser-banner"),
                  closeButton = document.querySelector(".unsupported-browser-banner__close");
              closeButton.addEventListener("click", function () {
                createCookie("unsupportedBrowserBannerDismissed", "1");
                unsupportedBrowserBanner.parentNode.removeChild(unsupportedBrowserBanner);
              });
            }`,
          '2URIy92WfK0Dn3MfJZDAS7'
        )
      }
    }
  }

  /**
   * Rule Groups Structure
   * ---------------------
   * decision = {
   *   "requestId": "b1bd4e50-a14d-4c6c-8652-d94d0d152865",
   *   "deviceId": "e9c6b3d2-e6e5-409d-9014-5cb0de1cf568",
   *   "ttl": 0,
   *   "placements": [{ ... }],
   *   "fromCache": true,
   *   "decisionId": "0Eks8TrOCRpETaVON0XQCs",
   *   "experienceId": "5TYfDXrJB8DaMy1LMm4AVG",
   *   "experienceNumber": 1,
   *   "testId": "0pMkVd3e4uI4Omv5qwpluE",
   *   "testName": "A/B Experience Switch",
   *   "audienceId": "5D6BebehgNHjd82y9wgrpz",
   *   "audienceName": "Make Page Viewed",
   *   "experienceName": "Main Product Page",
   *   "cache": { ... },
   *   "matchingRuleGroups": [
   *     {
   *       "ruleGroupId": "2D6DO4VJtCmss5V8zUoIpC",
   *       "name": "Desktop Users",
   *       "value": "OPTION_A"
   *     },
   *     {
   *       "ruleGroupId": "abc123",
   *       "name": "Make Button Click",
   *       // the errored property will be added if the DAPI decision for this rule group failed
   *       "errored": true
   *     }
   *   ]
   * }
   *
   * @param {object} decision
   */
  const decisionsReceived = decision => {
    // Extract the property from the decision.
    const { matchingRuleGroups = [] } = decision

    // Abort if no matching rule groups.
    if (!matchingRuleGroups || matchingRuleGroups.length < 1) {
      return
    }

    // Convert the array of non-errored groups into object.
    matches = matchingRuleGroups.reduce(
      (state, { errored, ruleGroupId, value }) => {
        if (!errored) {
          return Object.assign(state, { [ruleGroupId]: value })
        }
        return state
      },
      matches
    )

    // Handle the decisions on Cohesion's 'preamp:done' callback (after Preamp has placed it's assets and placements).
    window.cohesion('preamp:done', handleDecision)
  }

  // Attach to 'preamp:decisionsReceived' (the Preamp server has returned a decision and it is ready to be acted on).
  window.cohesion('preamp:decisionsReceived', decisionsReceived)
})()
